/**
 * @module cercaliagl/view/VAddress
 */

import $ from 'jquery';
import BaseView from './BaseView';
import { i18n } from '../dictionary';

/**
 * @typedef {Object} VAddressData
 * @property {string|undefined} postalcode
 * @property {string|undefined} municipality
 * @property {string|undefined} locality
 * @property {string|undefined} region
 * @property {string|undefined} subregion
 * @property {string|undefined} country
 * @property {string|undefined} txtCoord
 * @property {string|undefined} height
 * @property {string|undefined} mail
 * @property {string|undefined} tel
 * @property {string|undefined} web
 * @property {string|undefined} txtDirection
 * @property {string|undefined} txtCityPostalcode
 * @property {string|undefined} txtStreet
 * @property {string|undefined} kmh
 */

const State = {
  SUMMARY: 'summary',
  DETAILED: 'detailed',
};

/**
 * @enum {string}
 */
export const EventType = {
  DIRECTION_TO: 'direction_to',
  SAVE_GEOMETRY: 'save_geometry',
  CLOSED: 'closed',
};

/**
 * @classdesc
 */
class VAddress extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $(
      `<div class="v-address mdc-elevation--z4">
        <div class="data">
          <div class="summary">
            <div class="content"></div>
          </div>
          <div class="detailed">
            <div class="header">
              <span>${i18n('detailed')}</span>
            </div>
            <div class="content"></div>
          </div>
        </div>
        <div class="actions">
          <button type="button" class="mdc-icon-button info">
            <span class="mdc-icon-button__icon icon-info"></span>
          </button>
          <button type="button" class="mdc-icon-button routing">
            <span class="mdc-icon-button__icon icon-routing"></span>
          </button>
          <button type="button" class="mdc-icon-button save">
            <span class="mdc-icon-button__icon icon-save"></span>
          </button>
          <button type="button" class="mdc-button ok"><span class="mdc-button__ripple"></span>OK</button>
        </div>
       </div>`,
    );

    super(jqElement);

    /**
     * @private
     */
    this.currentState_ = State.SUMMARY;

    /**
     * @private
     * @type {import('../servers').GeEntity}
     */
    this.ge_ = null;

    /**
     * @private
     * @type {VAddressData}
     */
    this.data_ = null;
    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    this.jqElement_.find('button.info').click(() => this.toggle_());
    this.jqElement_.find('button.routing').click(() => this.emit(EventType.DIRECTION_TO, this.ge_));
    this.jqElement_.find('button.save').click(() => this.emit(EventType.SAVE_GEOMETRY, {}));
    this.jqElement_.find('button.ok').click(() => {
      this.hide(true);
      this.emit(EventType.CLOSED);
    });
  }

  /**
   * Reset.
   */
  reset() {
    this.currentState_ = State.SUMMARY;
    this.jqElement_.find('.detailed').hide();
    this.jqElement_.find('.summary').show();
    this.ge_ = null;
    this.detailed_ = false;
    this.data_ = null;
  }

  /**
   * @private
   */
  toggle_() {
    const jqDetailedDiv = this.jqElement_.find('.detailed');
    const jqSummaryDiv = this.jqElement_.find('.summary');
    if (this.currentState_ === State.SUMMARY) {
      jqSummaryDiv.hide();
      jqDetailedDiv.show();
      this.currentState_ = State.DETAILED;
    } else if (this.currentState_ === State.DETAILED) {
      jqDetailedDiv.hide();
      jqSummaryDiv.show();
      this.currentState_ = State.SUMMARY;
    }
  }

  /**
   * @param {import('../servers').GeEntity} ge GeEntity.
   * @param {string} type Ge Type. `adr`, `st` or `adr`.
   */
  setGeoEntity(ge, type) {
    const { coord } = ge;
    const postalcode = ge.type === 'pc' ? ge.name.value : (ge.postalcode ? ge.postalcode.id : null);
    const municipality = ge.municipality ? ge.municipality.value : null;
    const locality = ge.city ? ge.city.value : null;
    const locationSelected = ge.name ? ge.name.value : null;
    const kmh = ge.kmh && ge.kmh !== '0' ? ge.kmh : null;
    const pc2 = ge.pc2 ? ge.pc2 : null;
    const data = {
      postalcode,
      municipality,
      locality,
      region: ge.region ? ge.region.value : null,
      subregion: ge.subregion ? ge.subregion.value : null,
      country: ge.country ? ge.country.value : null,
      txtCoord: `${coord.y}, ${coord.x}`,
      height: ge.height ? ge.height : null,
      mail: null,
      tel: null,
      web: null,
      kmh,
      pc2,
    };

    const cityPostalCode = (postalcode ? `${postalcode} ` : '') + (locality || municipality || (locationSelected || ''));

    if (type === 'ct') {
      data.txtStreet = null;
      data.txtDirection = cityPostalCode;
      data.txtCityPostalcode = null;
    } else if (type === 'pc' || type === 'pc2') {
      data.txtDirection = `${cityPostalCode.trim()}, ${data.country}`;
      data.txtCityPostalcode = null;
      data.txtStreet = null;
    } else {
      const prefix = ge.prefix ? `${ge.prefix} ` : '';
      const article = ge.article ? ge.article + (ge.article.slice(-1) === "'" ? '' : ' ') : '';
      const km = ge.km ? ge.km.value : null;
      const street = ge.frc ? ge.name + (km ? `, Km ${km}` : '') : prefix + article + (ge.sname?.value ? ge.sname.value : ge.sname);
      const housenumber = ge.housenumber ? ge.housenumber.value : null;
      const intersectionStreet = ge.intersection && parseInt(ge.intersection.dist, 10) <= 25 ? ge.intersection.name : null;

      data.txtDirection = street + (housenumber ? `, ${housenumber}` : '') + (intersectionStreet ? ` / ${intersectionStreet}` : '');
      data.txtCityPostalcode = cityPostalCode;
      data.txtStreet = data.txtDirection;
    }
    this.data_ = /** @type {VAddressData} */ (data);
    this.ge_ = ge;
    this.render_();
  }

  /**
   * @param {import('../servers').GeEntity} ge GeEntity.
   */
  setPoiEntity(ge) {
    const { coord } = ge;
    const postalcode = ge.postalcode ? ge.postalcode.id : null;
    const municipality = ge.municipality ? ge.municipality.value : null;
    this.data_ = {
      postalcode,
      locality: null,
      municipality,
      region: ge.region ? ge.region.value : null,
      subregion: ge.subregion ? ge.subregion.value : null,
      country: ge.country.value,
      tel: ge.poi_data && ge.poi_data.tel ? ge.poi_data.tel : null,
      web: ge.poi_data && ge.poi_data.web ? (ge.poi_data.web.startsWith('http') ? ge.poi_data.web : `http://${ge.poi_data.web}`) : null,
      mail: ge.poi_data && ge.poi_data.mail ? ge.poi_data.mail : null,
      txtStreet: null,
      txtDirection: ge.name.value,
      txtCityPostalcode: (postalcode ? `${postalcode}, ` : '') + (municipality || ''),
      txtCoord: `${coord.x}, ${coord.y}`,
    };
    this.ge_ = ge;
    this.render_();
  }

  /**
   * @private
   */
  render_() {
    this.renderSummary_();
    this.renderDetailed_();
  }

  /**
   * @private
   */
  renderSummary_() {
    const html = `${this.data_.txtDirection && this.data_.txtDirection !== 'undefined' ? `<span class="direction">${this.data_.txtDirection}</span>` : ''}
    ${this.data_.pc2 ? `<span class="direction">${this.data_.pc2}</span>` : ''}
     ${this.data_.txtCityPostalcode ? `<span class="cp-city">${this.data_.txtCityPostalcode}</span>` : ''}
     ${this.data_.kmh ? `<div class="kmh-container"><span class="kmh">${this.data_.kmh}</span></div>` : ''}
     ${this.data_.tel ? `<span class="cp-city">Tel: ${this.data_.tel}</span>` : ''}
     ${this.data_.mail ? `<span class="cp-city">E-mail: <a href$="mailto:${this.data_.mail}">${this.data_.mail}</a></span>` : ''}
     ${this.data_.web ? `<span class="cp-city">Web: <a target="_blank" href$="${this.data_.web}">${this.data_.web}</a></span>` : ''}
     <div class="coord-altitude">
       <span class="coord">${this.data_.txtCoord}</span>
       ${this.data_.height ? `<span class="height">(${this.data_.height}m)</span>` : ''}
     </div>`;
    this.jqElement_.find('.summary .content').html(html);
  }

  /**
   * @private
   */
  renderDetailed_() {
    const html = `${this.data_.txtStreet && this.data_.txtStreet !== 'undefined' ? `<span><b>${i18n('street')}</b>: ${this.data_.txtStreet}</span>` : ''}
     ${this.data_.locality ? `<span><b>${i18n('city')}</b>: ${this.data_.locality}</span>` : ''}
     ${this.data_.postalcode ? `<span><b>${i18n('postalCode')}</b>: ${this.data_.postalcode}</span>` : ''}
     ${this.data_.municipality ? `<span><b>${i18n('municipality')}</b>: ${this.data_.municipality}</span>` : ''}
     ${this.data_.subregion ? `<span><b>${i18n('regionLevel2')}</b>: ${this.data_.subregion}</span>` : ''}
     ${this.data_.region ? `<span><b>${i18n('regionLevel1')}</b>: ${this.data_.region}</span>` : ''}
     <span><b>${i18n('country')}</b>: ${this.data_.country ?? this.data_.txtCityPostalcode}</span>
     ${this.data_.height ? `<span><b>${i18n('altitude')}</b>: ${this.data_.height}m</span>` : ''}
     ${this.data_.kmh ? `<span><b>${i18n('maximumSpeed')}</b>: ${this.data_.kmh} km/h</span>` : ''}
     <span><b>${i18n('xy')}</b>: ${this.data_.txtCoord}</span>`;

    this.jqElement_.find('.detailed .content').html(html);
  }

  /**
   * @return {import('../servers').GeEntity} ge GeEntity.
   */
  getGeoEntity() {
    return this.ge_;
  }

  /**
   * @param {boolean} visible Visible
   */
  toggleBtnSaveKml(visible) {
    const btn = this.jqElement_.find('button.save');
    if (visible) {
      btn.show();
    } else {
      btn.hide();
    }
  }

  /**
   * Click direction
   */
  clickDirectionTo() {
    this.jqElement_.find('button.routing').click();
    this.hide();
    this.reset();
  }
}

export default VAddress;
